import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Stack,
  Paper,
  alpha,
  useTheme,
  Switch,
  Divider,
  keyframes,
  Chip,
} from "@mui/material";
import {
  Visibility,
  Medication,
  CalendarMonth,
  FlashOn,
  LocationOn,
  Cake,
  HealthAndSafety,
  Psychology,
  Route,
  Badge as BadgeIcon,
  MonitorHeart,
  Assignment,
  Phone,
  Email,
} from "@mui/icons-material";
import { useQueryClient } from "react-query";

import maleImage from "../../images/male2.png";
import femaleImage from "../../images/female.png";
import NotFound from "../NotFound";
import { patchClient } from "../../api/services/Client";
import { QK } from "../../base/qk";
import { formatDate } from "../../utils/time";
import FlashMessageModal from "./Dialog/FlashMessageDialog";
import ClientSchedulesDrawer from "../Clients/Schedules/ClientSchedulesDrawer";

const spotlightEffect = keyframes`
  0% { 
    box-shadow: 0 0 0 0px rgba(244, 63, 94, 0.7);
    transform: scale(1);
  }
  50% { 
    box-shadow: 0 0 0 10px rgba(244, 63, 94, 0);
    transform: scale(1.15);
  }
  100% { 
    box-shadow: 0 0 0 0px rgba(244, 63, 94, 0);
    transform: scale(1);
  }
`;

const ClientList = ({ clickedCandidateId, data }) => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const [selectedClient, setSelectedClient] = useState(null);
  const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);

  if (data?.length === 0) return <NotFound />;

  const handleToggle = async (id, isActive) => {
    const response = await patchClient(id, { is_active: !isActive });
    if (response.status === 200) {
      queryClient.invalidateQueries(QK.clients);
    }
  };

  const handleOpenSchedules = (clientId) => {
    setSelectedClientId(clientId);
    setScheduleDrawerOpen(true);
  };

  const handleCloseSchedules = () => {
    setScheduleDrawerOpen(false);
    setSelectedClientId(null);
  };

  const getPrimaryPhone = (client) => {
    if (!client?.phones) return null;
    const primary = client.phones.find(p => p.preference === 1);
    return primary || client.phones[0];
  };

  const getPrimaryEmail = (client) => {
    if (!client?.emails) return null;
    const primary = client.emails.find(e => e.preference === 1);
    return primary || client.emails[0];
  };

  const getPrimaryAddress = (client) => {
    if (!client?.addresses) return null;
    const primary = client.addresses.find(addr => addr.is_service_delivery === true);
    return primary || client.addresses[0];
  };

  const calculateAge = (birthdate) => {
    if (!birthdate) return null;
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
      {data &&
        data.map((client) => {
          const todayStr = new Date().toISOString().split("T")[0];
          const isExpired = client.display_until && client.display_until < todayStr;
          const hasActiveFlash =
            client.flash_message && client.flash_message.trim() !== "" && !isExpired;

          const primaryPhone = getPrimaryPhone(client);
          const primaryEmail = getPrimaryEmail(client);
          const primaryAddress = getPrimaryAddress(client);
          const age = calculateAge(client?.birthdate);

          return (
            <Paper
              key={client.id}
              elevation={0}
              sx={{
                p: "14px 20px",
                borderRadius: 4,
                border: `1px solid ${
                  clickedCandidateId === client.id
                    ? theme.palette.primary.main
                    : alpha(theme.palette.divider, 0.1)
                }`,
                bgcolor:
                  clickedCandidateId === client.id
                    ? alpha(theme.palette.primary.main, 0.04)
                    : "#fff",
                transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
                "&:hover": {
                  boxShadow: "0 8px 24px rgba(149, 157, 165, 0.15)",
                  borderColor: theme.palette.primary.main,
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Stack
                direction="row"
                spacing={2.5}
                alignItems="center"
                sx={{ minWidth: "280px", flexShrink: 0 }}
              >
                <Avatar
                  src={client.profile_photo || (client.gender === "Male" ? maleImage : femaleImage)}
                  sx={{
                    width: 52,
                    height: 52,
                    border: `3px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    p: 0.2,
                    bgcolor: "#f1f5f9",
                  }}
                />
                <Box>
                  <Typography variant="subtitle1" fontWeight={900} sx={{ color: "#1e293b" }}>
                    {client.first_name} {client.last_name}
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.3 }} flexWrap="wrap">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Cake sx={{ fontSize: 14, color: "primary.main" }} />
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        {formatDate(client?.birthdate)}
                      </Typography>
                    </Stack>
                    {age !== null && (
                      <Chip
                        label={`${age} years`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.55rem",
                          bgcolor: alpha("#10b981", 0.08),
                          color: "#10b981",
                          fontWeight: 600,
                        }}
                      />
                    )}
                    {client.nhs_number && (
                      <Chip
                        label={`NHS: ${client.nhs_number}`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.55rem",
                          bgcolor: alpha("#6366f1", 0.08),
                          color: "#6366f1",
                          fontWeight: 600,
                        }}
                      />
                    )}
                    <Chip
                      label={client.is_active ? "Active" : "Inactive"}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.55rem",
                        bgcolor: client.is_active ? "#D1FAE5" : "#FEE2E2",
                        color: client.is_active ? "#039855" : "#EF4444",
                        fontWeight: 600,
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>

              <Box
                sx={{
                  flexGrow: 1,
                  px: 3,
                  display: { xs: "none", lg: "flex" },
                  flexDirection: "column",
                  gap: 0.5,
                  overflow: "hidden",
                }}
              >
                {primaryAddress && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <LocationOn sx={{ fontSize: 14, color: "text.disabled" }} />
                    <Typography variant="caption" fontWeight={600} color="text.secondary" noWrap>
                      {primaryAddress.address_1}
                      {primaryAddress.town ? `, ${primaryAddress.town}` : ''}
                      {primaryAddress.postcode ? `, ${primaryAddress.postcode}` : ''}
                    </Typography>
                  </Stack>
                )}
                <Stack direction="row" spacing={2} alignItems="center">
                  {primaryPhone && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Phone sx={{ fontSize: 12, color: "text.disabled" }} />
                      <Typography variant="caption" fontWeight={500} color="text.secondary" noWrap>
                        {primaryPhone.number}
                      </Typography>
                    </Stack>
                  )}
                  {primaryEmail && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Email sx={{ fontSize: 12, color: "text.disabled" }} />
                      <Typography variant="caption" fontWeight={500} color="text.secondary" noWrap>
                        {primaryEmail.email}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>

              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{
                  flexShrink: 0,
                  bgcolor: alpha("#6366f1", 0.03),
                  px: 1.5,
                  py: 0.8,
                  borderRadius: "16px",
                  border: `1px solid ${alpha("#6366f1", 0.08)}`,
                }}
              >
                <ActionButton
                  to={`/client/${client.id}/`}
                  icon={<Visibility />}
                  title="Full Details"
                  color="#6366f1"
                />
                <ActionButton
                  to={`/${client.id}/assessments/`}
                  icon={<HealthAndSafety />}
                  title="Care Plan"
                  color="#10b981"
                />
                <ActionButton
                  to={`/personal-details/${client.id}`}
                  icon={<BadgeIcon />}
                  title="Profile"
                  color="#f59e0b"
                />
                <ActionButton
                  to={`/observation/${client.id}/`}
                  icon={<Psychology />}
                  title="Observation"
                  color="#ec4899"
                />
                <ActionButton
                  to={`/visit-analysis/${client.id}/`}
                  icon={<Route />}
                  title="Visit Map"
                  color="#8b5cf6"
                />
                <ActionButton
                  to={`/medication/${client.id}/`}
                  icon={<Medication />}
                  title="Medication"
                  color="#ef4444"
                />
                <ActionButton
                  to={`/active-assessments/${client.id}/`}
                  icon={<MonitorHeart />}
                  title="Assessments"
                  color="#06b6d4"
                />
                <ActionButton
                  to={`/${client.id}/assessments/initial-assessment`}
                  icon={<Assignment />}
                  title="Initial Assessment"
                  color="#f472b6"
                />

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 0.5, height: 28, my: "auto", opacity: 0.6 }}
                />

                <Tooltip title="View Schedules" arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenSchedules(client.id)}
                    sx={{
                      color: "#6366f1",
                      width: 42,
                      height: 42,
                      bgcolor: alpha("#6366f1", 0.02),
                      transition: "0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        bgcolor: alpha("#6366f1", 0.12),
                        transform: "scale(1.2) rotate(5deg)",
                        boxShadow: `0 4px 10px ${alpha("#6366f1", 0.2)}`,
                      },
                    }}
                  >
                    <CalendarMonth sx={{ fontSize: 24 }} />
                  </IconButton>
                </Tooltip>

                <Tooltip
                  title={hasActiveFlash ? `CRITICAL: ${client.flash_message}` : "Flash Message"}
                >
                  <IconButton
                    size="small"
                    onClick={() => setSelectedClient(client)}
                    sx={{
                      color: hasActiveFlash ? "#fff" : "#f43f5e",
                      bgcolor: hasActiveFlash ? "#f43f5e" : "transparent",
                      animation: hasActiveFlash ? `${spotlightEffect} 1.8s infinite ease-in-out` : "none",
                      transition: "0.2s",
                      "&:hover": {
                        bgcolor: hasActiveFlash ? "#e11d48" : alpha("#f43f5e", 0.1),
                        transform: "scale(1.2)",
                      },
                    }}
                  >
                    <FlashOn sx={{ fontSize: 22 }} />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Stack direction="column" alignItems="center" sx={{ ml: 3, flexShrink: 0 }}>
                <Switch
                  size="medium"
                  checked={client.is_active}
                  onChange={() => handleToggle(client.id, client.is_active)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "success.main" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      bgcolor: "success.main",
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  fontWeight={900}
                  sx={{
                    fontSize: "0.6rem",
                    color: client.is_active ? "success.main" : "text.disabled",
                    mt: 0.2,
                  }}
                >
                  {client.is_active ? "ACTIVE" : "OFFLINE"}
                </Typography>
              </Stack>
            </Paper>
          );
        })}

      <FlashMessageModal client={selectedClient} onClose={() => setSelectedClient(null)} />

      <ClientSchedulesDrawer
        open={scheduleDrawerOpen}
        clientId={selectedClientId}
        onClose={handleCloseSchedules}
      />
    </Box>
  );
};

const ActionButton = ({ to, icon, title, color }) => (
  <Tooltip title={title} arrow placement="top">
    <IconButton
      component={Link}
      to={to}
      sx={{
        color: color,
        width: 42,
        height: 42,
        bgcolor: alpha(color, 0.02),
        transition: "0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          bgcolor: alpha(color, 0.12),
          transform: "scale(1.2) rotate(5deg)",
          boxShadow: `0 4px 10px ${alpha(color, 0.2)}`,
        },
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 24 } })}
    </IconButton>
  </Tooltip>
);

export default ClientList;